import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface Technology {
    id: string;
    name: string;
    description?: string;
    resourceTypeId: string;
}

export interface ResourceType {
    id: string;
    name: string;
    description?: string;
    technologies?: Technology[]; // Opcional, pode ser preenchido depois
}

export interface Seniority {
    id: string;
    name: string;
}

export function useResourceOptions() {
    const [types, setTypes] = useState<ResourceType[]>([]);
    const [technologies, setTechnologies] = useState<Technology[]>([]);
    const [seniorities, setSeniorities] = useState<Seniority[]>([]); // Estado para senioridades
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const typesSnapshot = await getDocs(collection(db, 'resourceTypes'));
            const fetchedTypes = typesSnapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
                id: doc.id,
                name: doc.data().name,
                description: doc.data().description ?? '',
                technologies: [] // Inicializa vazio, será preenchido depois
            } as ResourceType)).sort((a, b) => a.name.localeCompare(b.name));

            const technologiesSnapshot = await getDocs(collection(db, 'technologies'));
            const fetchedTechnologies = technologiesSnapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
                id: doc.id,
                name: doc.data().name,
                description: doc.data().description ?? '',
                resourceTypeId: doc.data().resourceTypeId
            } as Technology)).sort((a, b) => a.name.localeCompare(b.name));

            const senioritiesSnapshot = await getDocs(collection(db, 'seniorities'));
            const fetchedSeniorities = senioritiesSnapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
                id: doc.id,
                name: doc.data().name
            } as Seniority));

            // Sort seniorities: custom order then alphabetical
            const customOrder = ['Júnior', 'Pleno', 'Sênior', 'Especialista', 'Tech Lead', 'Arquiteto'];
            fetchedSeniorities.sort((a, b) => {
                const indexA = customOrder.indexOf(a.name);
                const indexB = customOrder.indexOf(b.name);

                if (indexA !== -1 && indexB !== -1) {
                    return indexA - indexB; // Both are in custom order
                } else if (indexA !== -1) {
                    return -1; // A is in custom order, B is not
                } else if (indexB !== -1) {
                    return 1; // B is in custom order, A is not
                } else {
                    return a.name.localeCompare(b.name); // Neither in custom order, sort alphabetically
                }
            });
            setSeniorities(fetchedSeniorities);

            // Associar tecnologias aos tipos
            const typesWithTechs = fetchedTypes.map(type => ({
                ...type,
                technologies: fetchedTechnologies.filter(tech => tech.resourceTypeId === type.id)
            }));

            setTypes(typesWithTechs);
            setTechnologies(fetchedTechnologies);

        } catch (err) {
            console.error("Erro ao buscar opções de recursos:", err);
            setError('Falha ao carregar dados. Tente novamente mais tarde.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getTechnologiesByType = useCallback((typeId: string) => {
        return technologies
            .filter(tech => tech.resourceTypeId === typeId)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [technologies]);

    return { types, technologies, seniorities, getTechnologiesByType, loading, error, refetch: fetchData };
} 
